// ─────────────────────────────────────────────────────────────
// JerryMarkerLaneBody — canvas body for a custom marker lane.
// Shared by both Group and GroupSustain lanes.
// Mirrors OpenDAW's MarkerTrackBody interaction model exactly.
// ─────────────────────────────────────────────────────────────

import {Lifecycle, Option} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {createElement} from "@opendaw/lib-jsx"
import {ContextMenu, MenuItem} from "@opendaw/studio-core"
import {Dragging, Events} from "@opendaw/lib-dom"
import {Surface} from "@/ui/surface/Surface"
import {FloatingTextInput} from "@/ui/components/FloatingTextInput"
import {jerryMarkerStore} from "./JerryMarkerStore"
import {JerryMarker, JerryLaneType, JERRY_DEFAULT_NAMES, JERRY_LANE_HUES} from "./JerryMarkerTypes"
import {JerryMarkerRenderer} from "./JerryMarkerRenderer"

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
    lane: JerryLaneType
}

export const JerryMarkerLaneBody = ({lifecycle, service, lane}: Construct) => {
    const {timeline, project} = service
    const {range, snapping} = timeline
    const engine = project.engine
    const hue = JERRY_LANE_HUES[lane]
    const defaultNames = JERRY_DEFAULT_NAMES[lane]

    const canvas: HTMLCanvasElement = <canvas style={{fontSize: "1.25em"}}/>

    // ── Renderer ───────────────────────────────────────────────

    const {requestUpdate} = lifecycle.own(
        JerryMarkerRenderer.createTrackRenderer(
            canvas,
            range,
            () => jerryMarkerStore.getMarkers(lane),
            () => engine.position.getValue(),
            hue
        )
    )

    // ── Context 2D for hit testing ─────────────────────────────

    let ctx2d: CanvasRenderingContext2D | null = null
    const getCtx = (): CanvasRenderingContext2D | null => {
        if (!ctx2d) {ctx2d = canvas.getContext("2d")}
        return ctx2d
    }

    const hitTest = (localX: number): JerryMarker | null => {
        const ctx = getCtx()
        if (!ctx) {return null}
        return JerryMarkerRenderer.hitTest(ctx, range, jerryMarkerStore.getMarkers(lane), localX)
    }

    // ── Next default name ──────────────────────────────────────

    const nextDefaultName = (): string => {
        const markers = jerryMarkerStore.getMarkers(lane)
        if (markers.length === 0) {return defaultNames[0]}
        const last = markers[markers.length - 1]
        const idx = defaultNames.indexOf(last.label)
        return idx === -1 ? "New" : (defaultNames[idx + 1] ?? "New")
    }

    // ── Subscribe to store / range / playhead changes ──────────

    lifecycle.own(jerryMarkerStore.subscribe(requestUpdate))
    lifecycle.own(range.subscribe(requestUpdate))
    lifecycle.own(engine.position.subscribe(requestUpdate))

    // ── Interactions ───────────────────────────────────────────

    let lastTimeDown = 0

    // Right-click context menu
    lifecycle.own(ContextMenu.subscribe(canvas, ({addItems, client}: ContextMenu.Collector) => {
        const rect = canvas.getBoundingClientRect()
        const localX = client.clientX - rect.left
        const marker = hitTest(localX)
        if (marker === null) {return}

        addItems(
            MenuItem.default({label: "Rename"}).setRuntimeChildrenProcedure(parent => {
                parent.addMenuItem(
                    MenuItem.default({label: "Custom"}).setTriggerProcedure(() => {
                        const resolvers = Promise.withResolvers<string>()
                        const clientRect = canvas.getBoundingClientRect()
                        Surface.get(canvas).flyout.appendChild(FloatingTextInput({
                            position: {
                                x: range.unitToX(marker.position) + clientRect.left,
                                y: clientRect.top + clientRect.height / 2
                            },
                            value: marker.label,
                            resolvers
                        }))
                        resolvers.promise.then(
                            newName => jerryMarkerStore.renameMarker(lane, marker.id, newName),
                            () => {}
                        )
                    }),
                    ...defaultNames.map((name, index) =>
                        MenuItem.default({
                            label: name,
                            separatorBefore: index === 0,
                            checked: name === marker.label
                        }).setTriggerProcedure(() =>
                            jerryMarkerStore.renameMarker(lane, marker.id, name)
                        )
                    )
                )
            }),
            MenuItem.default({label: "Delete"})
                .setTriggerProcedure(() => jerryMarkerStore.deleteMarker(lane, marker.id))
        )
    }))

    // Drag to move / double-click to add
    lifecycle.own(Dragging.attach(canvas, (startEvent: PointerEvent) => {
        const now = Date.now()
        const isDblClick = now - lastTimeDown < Events.DOUBLE_DOWN_THRESHOLD
        lastTimeDown = now

        const rect = canvas.getBoundingClientRect()
        const localX = startEvent.clientX - rect.left
        const marker = hitTest(localX)

        if (marker === null) {
            // Double-click on empty area → add marker
            if (isDblClick) {
                const position = snapping.xToUnitFloor(localX)
                const existing = jerryMarkerStore.getMarkers(lane).find(m => m.position === position)
                if (!existing) {
                    jerryMarkerStore.addMarker(lane, position, nextDefaultName())
                }
            }
            return Option.None
        }

        // Drag existing marker
        const originalPosition = marker.position

        return Option.wrap({
            update: (event: Dragging.Event) => {
                const rect = canvas.getBoundingClientRect()
                const position = snapping.xToUnitFloor(event.clientX - rect.left)
                jerryMarkerStore.moveMarker(lane, marker.id, position)
            },
            cancel: () => jerryMarkerStore.moveMarker(lane, marker.id, originalPosition),
            approve: () => {}
        } satisfies Dragging.Process)
    }))

    return (
        <div style={{
            gridColumn: "-1",
            fontSize: "0.5em",
            position: "relative",
            backgroundColor: "var(--color-panel-background)",
        }}>
            {canvas}
        </div>
    )
}
